import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, BarChart3, Store, Factory, Users, ShieldCheck, Bot,
  BookOpen, Sparkles, Bell, Settings, Waves, MessageSquare, AlertTriangle, Bug,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Executive Dashboard", end: true },
  {
    label: "Business Intelligence", icon: BarChart3, base: "/bi",
    children: [
      ["Revenue", "/bi/revenue"], ["Orders", "/bi/orders"], ["Services", "/bi/services"],
      ["Customers", "/bi/customers"], ["Growth", "/bi/growth"],
    ],
  },
  {
    label: "Hub Intelligence", icon: Store, base: "/hubs",
    children: [["Hub Overview", "/hubs/overview"], ["Performance", "/hubs/performance"], ["Capacity", "/hubs/capacity"], ["Productivity", "/hubs/productivity"]],
  },
  {
    label: "Factory Intelligence", icon: Factory, base: "/factory",
    children: [["Production", "/factory/production"], ["Machines", "/factory/machines"], ["Capacity", "/factory/capacity"], ["Chemicals", "/factory/chemicals"], ["Factory Quality", "/factory/quality"]],
  },
  {
    label: "Customer Intelligence", icon: Users, base: "/customers",
    children: [["Customer 360", "/customers/directory"], ["Segments", "/customers/segments"], ["Retention", "/customers/retention"], ["Churn", "/customers/churn"]],
  },
  {
    label: "Quality & SLA", icon: ShieldCheck, base: "/quality",
    children: [["Quality", "/quality/quality"], ["Complaints", "/quality/complaints"], ["SLA", "/quality/sla"], ["Escalations", "/quality/escalations"]],
  },
  {
    label: "AI Control Center", icon: Bot, base: "/ai",
    children: [["Overview", "/ai/overview"], ["Conversation History", "/ai/history"], ["Reported Chats", "/ai/reported-chats"], ["AI Error Log", "/ai/error-logs"], ["Customer-Care AI", "/ai/customer-care"], ["Retail AI", "/ai/retail"], ["AI Quality", "/ai/quality"], ["AI ROI", "/ai/roi"]],
  },
  {
    label: "Knowledge Base", icon: BookOpen, base: "/kb",
    children: [["Explore", "/kb/explore"], ["Search", "/kb/search"], ["Documents", "/kb/documents"], ["Upload", "/kb/upload"], ["Approvals", "/kb/approvals"], ["Health", "/kb/health"]],
  },
  { to: "/ask", icon: Sparkles, label: "Ask Your Business" },
  { to: "/insights", icon: Bell, label: "Insights & Alerts" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

const OPERATIONS_NAV = [
  { to: "/ai/history", icon: MessageSquare, label: "Conversation History" },
  { to: "/ai/reported-chats", icon: AlertTriangle, label: "Reported Chats" },
  { to: "/ai/error-logs", icon: Bug, label: "AI Error Log" },
];

export function SidebarNav({ onNavigate }) {
  const loc = useLocation();
  const { user } = useAuth();
  const isOperations = user?.role === "operations";
  const navItems = isOperations ? OPERATIONS_NAV : NAV;

  return (
    <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-3 space-y-0.5">
      {navItems.map((item) =>
        item.children ? (
          <div key={item.label} className="pt-2">
            <div className="flex items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
              <item.icon className="w-3.5 h-3.5" /> {item.label}
            </div>
            {item.children.map(([label, to]) => (
              <NavLink key={to} to={to} data-testid={`nav-${to}`} onClick={onNavigate}
                className={({ isActive }) => cn("block pl-8 pr-2 py-1.5 rounded-md text-[13px] font-medium transition-colors",
                  isActive || loc.pathname === to ? "bg-accent text-primary" : "text-slate-600 hover:bg-slate-50")}>
                {label}
              </NavLink>
            ))}
          </div>
        ) : (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={`nav-${item.to}`} onClick={onNavigate}
            className={({ isActive }) => cn("flex items-center gap-2.5 px-2 py-2 rounded-md text-[13px] font-semibold transition-colors",
              isActive || loc.pathname === item.to ? "bg-primary text-white" : "text-slate-700 hover:bg-slate-50")}>
            <item.icon className="w-4 h-4" /> {item.label}
          </NavLink>
        )
      )}
    </nav>
  );
}

export function SidebarBrand() {
  const { user } = useAuth();
  const isOperations = user?.role === "operations";

  return (
    <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-100 shrink-0">
      <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
        <Waves className="w-5 h-5 text-white" />
      </div>
      <div className="leading-tight">
        <div className="font-heading font-bold text-sm text-slate-900 tracking-tight">Control Tower</div>
        <div className="text-[10px] text-slate-400 uppercase tracking-wider">
          {isOperations ? "Operations Portal" : "Laundry Intelligence"}
        </div>
      </div>
    </div>
  );
}


export default function Sidebar() {
  return (
    <aside className="w-64 shrink-0 h-screen sticky top-0 bg-white border-r border-slate-200 hidden lg:flex flex-col" data-testid="sidebar">
      <SidebarBrand />
      <SidebarNav />
    </aside>
  );
}
